import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FiEdit2, FiLoader, FiMessageSquare, FiPlus, FiSave, FiTrash2, FiX } from "react-icons/fi";
import ManHourCell from "./ManHourCell";
import {
  buildEntryCellKey,
  formatDateKey,
  formatHours,
  formatShortDateLabel,
  formatWeekdayLabel,
  getDailyHoursTotals,
  getEntityId,
} from "../../utils/timesheetUtils";

const getSelectStyles = (isDark) => ({
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    borderRadius: "8px",
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    borderColor: state.isFocused ? "#6366f1" : isDark ? "#4b5563" : "#c7d2fe",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(99, 102, 241, 0.18)" : "none",
    ":hover": { borderColor: state.isFocused ? "#6366f1" : isDark ? "#6b7280" : "#a5b4fc" },
  }),
  singleValue: (base) => ({ ...base, color: isDark ? "#f3f4f6" : "#1f2937" }),
  input: (base) => ({ ...base, color: isDark ? "#f3f4f6" : "#1f2937" }),
  placeholder: (base) => ({ ...base, color: isDark ? "#9ca3af" : "#6b7280" }),
  menu: (base) => ({ ...base, zIndex: 50, backgroundColor: isDark ? "#111827" : "#ffffff" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    color: isDark ? "#f3f4f6" : "#1f2937",
    backgroundColor: state.isSelected
      ? isDark ? "#4338ca" : "#e0e7ff"
      : state.isFocused
      ? isDark ? "#374151" : "#eef2ff"
      : "transparent",
  }),
});

const SearchableSelect = ({ options, value, onChange, disabled, loading = false, placeholder, noOptionsText, styles, ariaLabel }) => {
  const normalizedOptions = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option
  );
  const selectedOption = normalizedOptions.find((option) => option.value === value) || null;

  return (
    <Select
      className="min-w-0 flex-1 text-left"
      aria-label={ariaLabel}
      isClearable
      isSearchable
      isDisabled={disabled}
      isLoading={loading}
      options={normalizedOptions}
      value={selectedOption}
      styles={styles}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
      placeholder={placeholder}
      noOptionsMessage={() => noOptionsText}
      onChange={(option) => onChange(option?.value || "")}
    />
  );
};

const ActionButton = ({ icon, label, onClick, tone = "default", disabled = false, iconOnly = false }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={iconOnly ? label : undefined} aria-label={iconOnly ? label : undefined} className={[
    "inline-flex items-center justify-center rounded-lg text-xs font-medium transition",
    iconOnly ? "p-2" : "gap-1.5 px-2.5 py-1.5",
    tone === "danger"
      ? "text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10"
      : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/60",
    disabled ? "cursor-not-allowed opacity-50" : "",
  ].join(" ")}>
    {icon}{!iconOnly && label}
  </button>
);

const ProjectSelect = ({ options, value = "", saving, onChange, onCancel, label, styles }) => (
  <div className="flex min-w-[260px] items-center gap-2">
    <SearchableSelect options={options} value={value} onChange={onChange} disabled={saving} placeholder={label} noOptionsText="ไม่พบ Project" styles={styles} ariaLabel={label} />
    {onCancel && <button type="button" onClick={onCancel} disabled={saving} aria-label="ยกเลิก" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"><FiX /></button>}
  </div>
);

const RemarkEditor = ({ value, saving, onChange, onSave, onCancel }) => (
  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
    <label className="text-xs font-semibold text-amber-900 dark:text-amber-100">Remark (ไม่บังคับ)</label>
    <textarea rows={2} value={value} onChange={(event) => onChange(event.target.value)} disabled={saving} placeholder="เช่น รอไฟล์จากลูกค้า" className="mt-2 w-full resize-y rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
    <div className="mt-2 flex justify-end gap-2">
      <button type="button" onClick={onCancel} disabled={saving} className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white dark:text-gray-300 dark:hover:bg-gray-700">ยกเลิก</button>
      <button type="button" onClick={onSave} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-60">{saving ? <FiLoader className="animate-spin" /> : <FiSave />}{saving ? "กำลังบันทึก..." : "บันทึก Remark"}</button>
    </div>
  </div>
);

const TimesheetTable = ({
  viewMode,
  days,
  rows,
  clients = [],
  clientsLoading = false,
  selectedClientId = "",
  pendingRows = [],
  cellStatuses,
  draftEntries = {},
  projectOptionsByClient = {},
  saving = false,
  locked = false,
  emptyMessage,
  onAddPendingRow,
  onAddPendingProjectRow,
  onPendingClientChange,
  onPendingProjectChange,
  onPendingHoursChange,
  onRemovePendingRow,
  onEditProject,
  onSaveRemark,
  onArchiveProject,
  onDraftChange,
}) => {
  const [editingProjectId, setEditingProjectId] = useState("");
  const [remarkProjectId, setRemarkProjectId] = useState("");
  const [remarkValue, setRemarkValue] = useState("");
  const [savingAction, setSavingAction] = useState("");
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const syncTheme = () => setIsDark(root.classList.contains("dark"));
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const searchableSelectStyles = useMemo(() => getSelectStyles(isDark), [isDark]);
  const clientOptions = useMemo(
    () => clients.map((client) => ({
      value: getEntityId(client._id || client.id),
      label: client.customerName || client.companyBaseName || "ไม่ระบุชื่อลูกค้า",
    })),
    [clients]
  );

  const dailyTotals = getDailyHoursTotals(rows, days);
  const totalHours = Object.values(dailyTotals).reduce((sum, hours) => sum + hours, 0);
  const projectRows = rows.flatMap((client) =>
    (client.projects || []).map((project) => ({ client, project }))
  );
  const reusableClientIds = Array.from(
    new Set([
      ...pendingRows.map((row) => row.clientId),
      ...projectRows.map(({ client }) => getEntityId(client.clientId)),
    ].filter(Boolean))
  );
  const reusableClientId =
    selectedClientId || (reusableClientIds.length === 1 ? reusableClientIds[0] : "");

  const availableOptions = (client, currentProjectId = "") => {
    const usedNames = new Set(
      (client.projects || [])
        .filter((project) => project.projectId !== currentProjectId)
        .map((project) => String(project.name || "").trim().toLocaleLowerCase())
    );
    return (projectOptionsByClient[client.clientId] || []).filter(
      (name) => !usedNames.has(String(name).trim().toLocaleLowerCase())
    );
  };

  const pendingProjectOptions = (pendingRow) => {
    if (!pendingRow.clientId) return [];
    const persistedClient = rows.find(
      (client) => getEntityId(client.clientId) === pendingRow.clientId
    ) || { clientId: pendingRow.clientId, projects: [] };
    const selectedByOtherRows = new Set(
      pendingRows
        .filter((row) => row.tempId !== pendingRow.tempId && row.clientId === pendingRow.clientId)
        .map((row) => String(row.projectName || "").trim().toLocaleLowerCase())
    );
    return availableOptions(persistedClient).filter(
      (name) => !selectedByOtherRows.has(String(name).trim().toLocaleLowerCase())
    );
  };

  const runAction = async (key, action) => {
    setSavingAction(key);
    try { return (await action()) !== false; } finally { setSavingAction(""); }
  };

  const renderProject = (client, project) => {
    const isEditing = editingProjectId === project.projectId;
    const isRemarkEditing = remarkProjectId === project.projectId;
    const editKey = `edit:${project.projectId}`;
    const remarkKey = `remark:${project.projectId}`;
    const deleteKey = `delete:${project.projectId}`;
    const editOptions = Array.from(
      new Set([project.name, ...availableOptions(client, project.projectId)].filter(Boolean))
    );

    return (
      <div className="min-w-0">
        {isEditing ? (
          <ProjectSelect options={editOptions} value={project.name} saving={savingAction === editKey} label="ค้นหาและเลือก Project ใหม่" styles={searchableSelectStyles} onCancel={() => setEditingProjectId("")} onChange={async (name) => { if (!name || name === project.name) return; const ok = await runAction(editKey, () => onEditProject(project, name)); if (ok) setEditingProjectId(""); }} />
        ) : (
          <div className="min-w-0">
            <p className="whitespace-normal break-words font-medium leading-5 text-gray-800 dark:text-gray-100">{project.name}</p>
            {project.remark && <p className="mt-1 whitespace-pre-wrap break-words text-xs text-amber-700 dark:text-amber-300">Remark: {project.remark}</p>}
            {!project.isCreating && viewMode !== "month" && <div className="mt-1.5 flex flex-wrap items-center gap-1"><ActionButton icon={<FiEdit2 size={12} />} label="แก้ไข" disabled={locked || Boolean(savingAction)} onClick={() => setEditingProjectId(project.projectId)} /><ActionButton icon={<FiMessageSquare size={12} />} label={project.remark ? "แก้ไข Remark" : "Remark"} disabled={locked || Boolean(savingAction)} onClick={() => { setRemarkProjectId(project.projectId); setRemarkValue(project.remark || ""); }} /><ActionButton icon={savingAction === deleteKey ? <FiLoader className="animate-spin" size={14} /> : <FiTrash2 size={14} />} label={savingAction === deleteKey ? "กำลังลบ..." : "ลบ"} iconOnly tone="danger" disabled={locked || Boolean(savingAction)} onClick={() => runAction(deleteKey, () => onArchiveProject(project))} /></div>}
          </div>
        )}
        {isRemarkEditing && <RemarkEditor value={remarkValue} saving={savingAction === remarkKey} onChange={setRemarkValue} onCancel={() => setRemarkProjectId("")} onSave={async () => { const ok = await runAction(remarkKey, () => onSaveRemark(project, remarkValue)); if (ok) setRemarkProjectId(""); }} />}
      </div>
    );
  };

  if (viewMode === "month") {
    return (
      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"><div className="overflow-x-auto"><table className="min-w-[760px] w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900/60"><tr><th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">ลูกค้า</th><th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Project</th><th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">ชั่วโมงรวม</th></tr></thead>
        <tbody>{projectRows.length === 0 ? <tr><td colSpan={3} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</td></tr> : projectRows.map(({ client, project }, index) => <tr key={project.projectId} className={`border-t border-gray-100 dark:border-gray-700/80 ${index % 2 === 0 ? "bg-white dark:bg-[#182236]" : "bg-slate-50 dark:bg-[#202a40]"}`}><td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-200">{client.clientName}</td><td className="px-4 py-3">{renderProject(client, project)}</td><td className="px-4 py-3 text-right font-semibold text-indigo-900 dark:text-indigo-100">{formatHours(project.totalHours)}</td></tr>)}</tbody>
      </table></div></div>
    );
  }

  return (
    <div className="mb-3 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div><p className="text-sm font-semibold text-gray-900 dark:text-gray-100">รายการเวลาทำงาน</p><p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">เพิ่มลูกค้าและ Project ได้หลายแถว แล้วบันทึกพร้อมกัน</p></div>
        <button type="button" onClick={onAddPendingRow} disabled={locked || saving} className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-indigo-500 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"><FiPlus /> เพิ่มลูกค้า</button>
      </div>
      <div className="max-w-full overflow-x-auto pb-5"><table className="w-full min-w-[1360px] table-fixed text-sm">
        <colgroup><col className="w-[240px]" /><col className="w-[360px]" />{days.map((day) => <col key={`column-${formatDateKey(day)}`} className="w-[96px]" />)}<col className="w-[88px]" /></colgroup>
        <thead className="bg-gray-50 dark:bg-gray-900/60"><tr><th className="sticky left-0 z-10 min-w-[260px] bg-gray-50 px-4 py-3 text-left font-semibold text-gray-700 dark:bg-gray-900/60 dark:text-gray-200">ลูกค้า</th><th className="min-w-[360px] px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200"><div className="flex items-center justify-between gap-3"><span>Project</span><button type="button" onClick={() => onAddPendingProjectRow(reusableClientId)} disabled={locked || saving || !reusableClientId} title={reusableClientId ? "เพิ่ม Project ให้ลูกค้านี้" : "เลือกลูกค้าก่อน"} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-700 dark:bg-gray-800 dark:text-indigo-300"><FiPlus size={13} /> เพิ่ม Project ให้ลูกค้านี้</button></div></th>{days.map((day) => <th key={formatDateKey(day)} className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-200"><div>{formatWeekdayLabel(day)}</div><div className="mt-1 text-xs font-normal text-gray-500 dark:text-gray-400">{formatShortDateLabel(day)}</div></th>)}<th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">ชั่วโมงรวม</th></tr></thead>
        <tbody>
          {pendingRows.map((pendingRow) => {
            const rowOptions = pendingProjectOptions(pendingRow);
            const pendingTotal = Object.values(pendingRow.hours || {}).reduce((sum, value) => sum + Number(value || 0), 0);
            return <tr key={pendingRow.tempId} className="border-t border-indigo-100 bg-indigo-50/70 dark:border-indigo-900/60 dark:bg-indigo-500/10">
              <td className="sticky left-0 z-[2] bg-indigo-50 px-4 py-3 dark:bg-[#202a40]">{pendingRow.lockClient ? <div className="rounded-lg border border-indigo-200 bg-indigo-100/70 px-3 py-2 text-sm font-medium text-indigo-900 dark:border-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-100">{clientOptions.find((option) => option.value === pendingRow.clientId)?.label || "ลูกค้าที่เลือก"}</div> : <SearchableSelect options={clientOptions} value={pendingRow.clientId} onChange={(clientId) => onPendingClientChange(pendingRow.tempId, clientId)} disabled={saving} loading={clientsLoading} placeholder={clientsLoading ? "กำลังโหลดลูกค้า..." : "ค้นหาและเลือกลูกค้า"} noOptionsText="ไม่พบลูกค้า" styles={searchableSelectStyles} ariaLabel="ค้นหาและเลือกลูกค้า" />}</td>
              <td className="px-4 py-3"><div className="flex items-center gap-2"><SearchableSelect options={rowOptions} value={pendingRow.projectName} onChange={(projectName) => onPendingProjectChange(pendingRow.tempId, projectName)} disabled={!pendingRow.clientId || saving} placeholder={!pendingRow.clientId ? "เลือกลูกค้าก่อน" : "ค้นหาและเลือก Project"} noOptionsText={!pendingRow.clientId ? "กรุณาเลือกลูกค้าก่อน" : "ไม่มี Project ที่เพิ่มได้"} styles={searchableSelectStyles} ariaLabel="ค้นหาและเลือก Project" /><button type="button" onClick={() => onRemovePendingRow(pendingRow.tempId)} disabled={saving} aria-label="ลบแถว" className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-60 dark:hover:bg-red-500/10"><FiTrash2 /></button></div></td>
              {days.map((day) => { const dateKey = formatDateKey(day); const cellKey = `${pendingRow.tempId}:${dateKey}`; return <td key={cellKey} className="px-3 py-3 align-top"><ManHourCell cellKey={cellKey} value={0} draftValue={pendingRow.hours?.[dateKey] ?? ""} status={pendingRow.hours?.[dateKey] ? "draft" : ""} disabled={locked || saving} onDraftChange={(hours) => onPendingHoursChange(pendingRow.tempId, dateKey, hours)} /></td>; })}
              <td className="px-4 py-3 text-center font-semibold text-indigo-900 dark:text-indigo-100">{formatHours(pendingTotal)}</td>
            </tr>;
          })}
          {projectRows.length === 0 && pendingRows.length === 0 ? <tr><td colSpan={days.length + 3} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</td></tr> : projectRows.map(({ client, project }, index) => {
            const projectId = getEntityId(project.projectId);
            const rowTone = index % 2 === 0 ? "bg-white dark:bg-[#182236]" : "bg-slate-50 dark:bg-[#202a40]";
            return <tr key={projectId} className={`border-t border-gray-100 dark:border-gray-700/80 ${rowTone}`}><td className={`sticky left-0 z-[1] px-4 py-3 font-medium text-gray-700 dark:text-gray-200 ${rowTone}`}>{client.clientName}</td><td className="px-4 py-3">{renderProject(client, project)}</td>{days.map((day) => { const dateKey = formatDateKey(day); const cellKey = buildEntryCellKey(projectId, dateKey); return <td key={cellKey} className="px-3 py-3 align-top"><ManHourCell cellKey={cellKey} value={project.dailyHours?.[dateKey] || 0} draftValue={draftEntries[cellKey]?.hours} status={cellStatuses[cellKey]} disabled={locked || saving || project.isCreating || project.isActive === false} onDraftChange={(hours) => onDraftChange({ clientId: getEntityId(client.clientId), projectId, workDate: dateKey, hours })} /></td>; })}<td className="px-4 py-3 text-center font-semibold text-indigo-900 dark:text-indigo-100">{formatHours(project.totalHours)}</td></tr>;
          })}
        </tbody>
        <tfoot className="border-t-2 border-indigo-200 bg-indigo-100 dark:border-indigo-900/70 dark:bg-[#3a447b]"><tr><td colSpan={2} className="sticky left-0 z-[1] bg-indigo-100 px-4 py-3 font-semibold text-indigo-900 dark:bg-[#3a447b] dark:text-indigo-100">รวมรายวัน</td>{days.map((day) => { const dateKey = formatDateKey(day); const pendingHours = pendingRows.reduce((sum, row) => sum + Number(row.hours?.[dateKey] || 0), 0); return <td key={dateKey} className="px-3 py-3 text-center font-semibold text-indigo-900 dark:text-indigo-100">{formatHours(Number(dailyTotals[dateKey] || 0) + pendingHours)}</td>; })}<td className="px-4 py-3 text-center font-bold text-indigo-950 dark:text-indigo-50">{formatHours(totalHours + pendingRows.reduce((sum, row) => sum + Object.values(row.hours || {}).reduce((rowSum, value) => rowSum + Number(value || 0), 0), 0))}</td></tr></tfoot>
      </table></div>
    </div>
  );
};

export default TimesheetTable;
